/**
 * Updates product-card media, price, fulfillment badge, and quick-buy target
 * when a shopper selects a variant swatch.
 */
(function () {
  if (window.__ileeraProductCardVariants) {
    return;
  }
  window.__ileeraProductCardVariants = true;

  var IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];
  var SWATCH_SELECTOR = '.product-card__swatches input[type="radio"]';

  // card element -> token of the most recent swap request for that card.
  // Lets a fast second click discard a slower first click's result.
  var latestRequest = new WeakMap();

  function toAbsoluteUrl(src) {
    return src && src.indexOf('//') === 0 ? 'https:' + src : src;
  }

  function buildSrcset(media) {
    var url;
    try {
      url = new URL(toAbsoluteUrl(media.src));
    } catch (err) {
      return '';
    }
    var maxWidth = media.width || 0;
    return IMAGE_WIDTHS.filter(function (width) {
      return width <= maxWidth;
    })
      .map(function (width) {
        url.searchParams.set('width', width);
        return url.href + ' ' + width + 'w';
      })
      .join(', ');
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Preload before replacing the visible card image.
  function preload(src) {
    return new Promise(function (resolve, reject) {
      var probe = new Image();
      var settled = false;

      function settleResolve() {
        if (settled) {
          return;
        }
        settled = true;
        resolve(probe);
      }

      probe.onload = settleResolve;
      probe.onerror = function () {
        if (settled) {
          return;
        }
        settled = true;
        reject(new Error('ileera: variant image load failed'));
      };

      probe.src = src;

      if (probe.complete && probe.naturalWidth > 0) {
        settleResolve();
      }
    });
  }

  function readMedia(input, attr) {
    var raw = input.getAttribute(attr);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  function applyImage(imgEl, media) {
    if (!imgEl || !media || !media.src) {
      return;
    }
    var absoluteSrc = toAbsoluteUrl(media.src);
    imgEl.src = absoluteSrc;
    imgEl.srcset = buildSrcset(media);
    if (media.width) {
      imgEl.width = media.width;
    }
    if (media.height) {
      imgEl.height = media.height;
    }
    imgEl.alt = media.alt || imgEl.alt || '';
    imgEl.dataset.ileeraVariantSrc = absoluteSrc;
  }

  function lastTextNode(el) {
    var nodes = el.childNodes;
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].nodeType === Node.TEXT_NODE) {
        return nodes[i];
      }
    }
    return null;
  }

  function swapCardImage(card, input, requestToken) {
    var primaryMedia = readMedia(input, 'data-ileera-variant-media');
    if (!primaryMedia) {
      return;
    }

    var primaryImg = card.querySelector('.product-card__image--primary');
    if (!primaryImg) {
      return;
    }

    var targetSrc = toAbsoluteUrl(primaryMedia.src);
    if (primaryImg.dataset.ileeraVariantSrc === targetSrc) {
      return;
    }

    var secondaryMedia = readMedia(input, 'data-ileera-variant-secondary-media');
    var secondaryImg = card.querySelector('.product-card__image--secondary');

    preload(targetSrc)
      .then(function () {
        if (latestRequest.get(card) !== requestToken) {
          return;
        }

        function commit() {
          if (latestRequest.get(card) !== requestToken) {
            return;
          }
          applyImage(primaryImg, primaryMedia);
          if (secondaryMedia && secondaryImg) {
            applyImage(secondaryImg, secondaryMedia);
          }
        }

        if (prefersReducedMotion()) {
          commit();
          return;
        }

        // A CSS transition keeps the final opacity state deterministic.
        primaryImg.style.transition = 'opacity 120ms ease-in';
        primaryImg.style.opacity = '0';

        var fadeOutSettled = new Promise(function (resolve) {
          var settled = false;
          function settle() {
            if (settled) {
              return;
            }
            settled = true;
            primaryImg.removeEventListener('transitionend', settle);
            resolve();
          }
          primaryImg.addEventListener('transitionend', settle);
          setTimeout(settle, 200);
        });

        fadeOutSettled.then(function () {
          if (latestRequest.get(card) !== requestToken) {
            return;
          }
          commit();
          primaryImg.style.transition = 'opacity 120ms ease-out';
          primaryImg.style.opacity = '1';
        });
      })
      .catch(function () {
        // Keep the current image if the selected image cannot load.
      });
  }

  function updateFulfillmentBadge(card, input) {
    var badge = card.querySelector('[data-ileera-fulfillment-badge]');
    if (!badge) {
      return;
    }

    var status = input.getAttribute('data-ileera-fulfillment-status');
    if (!status) {
      return;
    }

    // "Fast shipping" is a positive benefit, not an internal fulfillment
    // status. Made-to-order and unavailable variants intentionally show no
    // fulfillment badge on storefront cards.
    if (status !== 'ready') {
      badge.hidden = true;
      return;
    }

    var label = badge.getAttribute('data-ready-label');
    if (!label) {
      return;
    }

    var labelElement = badge.querySelector('[data-ileera-badge-label]');
    if (labelElement) {
      labelElement.textContent = label;
    }
    badge.hidden = false;
  }

  function updatePrice(card, input) {
    var priceText = input.getAttribute('data-variant-price');
    if (priceText) {
      var saleEl = card.querySelector('sale-price');
      var saleTextNode = saleEl && lastTextNode(saleEl);
      if (saleTextNode && saleTextNode.textContent.trim() !== priceText.trim()) {
        saleTextNode.textContent = priceText;
      }
    }

    var compareText = input.getAttribute('data-variant-compare-at-price');
    if (compareText) {
      var compareEl = card.querySelector('compare-at-price');
      var compareTextNode = compareEl && lastTextNode(compareEl);
      if (compareTextNode && compareTextNode.textContent.trim() !== compareText.trim()) {
        compareTextNode.textContent = compareText;
      }
    }
  }

  function updateQuickBuyTarget(card, input) {
    var variantId = input.getAttribute('data-variant-id');
    var handle = card.getAttribute('handle');
    if (!variantId || !handle) {
      return;
    }

    var trigger = card.querySelector('[aria-controls]');
    if (!trigger) {
      return;
    }

    var modal = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!modal || modal.tagName.toLowerCase() !== 'quick-buy-modal') {
      return;
    }

    modal.setAttribute('handle', handle + '?variant=' + variantId);
  }

  // Seed the deduplication marker from the image currently displayed.
  function seedInitialState() {
    document.querySelectorAll('.product-card__image--primary').forEach(function (img) {
      var src = img.getAttribute('src');
      if (src) {
        img.dataset.ileeraVariantSrc = toAbsoluteUrl(src);
      }
    });

    document.querySelectorAll('.product-card__image--secondary').forEach(function (img) {
      var src = img.getAttribute('src');
      if (src) {
        img.dataset.ileeraVariantSrc = toAbsoluteUrl(src);
      }
    });
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches(SWATCH_SELECTOR)) {
      return;
    }

    var card = input.closest('product-card');
    if (!card) {
      return;
    }

    var requestToken = {};
    latestRequest.set(card, requestToken);

    swapCardImage(card, input, requestToken);
    updatePrice(card, input);
    updateFulfillmentBadge(card, input);
    updateQuickBuyTarget(card, input);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seedInitialState);
  } else {
    seedInitialState();
  }
})();

